<!--
  v3.5 (May 12) Reserve N units of an inventory record for a job.
  POSTs to /parts/inventory/{id}/reserve/.
-->
<template>
  <v-dialog v-model="visible" max-width="520" persistent>
    <v-card v-if="item">
      <v-card-title class="d-flex align-center pa-3">
        <v-icon class="me-2" color="amber-darken-2">mdi-lock-clock</v-icon>
        Reserve stock for a job
        <v-spacer />
        <v-btn icon="mdi-close" variant="text" density="comfortable" @click="close" />
      </v-card-title>
      <v-divider />
      <v-card-text class="pa-3">
        <div class="text-body-2 mb-1">
          <strong>{{ item.part_number || ('Part #' + item.part_id) }}</strong>
          <span v-if="item.part_name"> &mdash; {{ item.part_name }}</span>
        </div>
        <div class="text-caption text-secondary mb-3">
          {{ item.warehouse_name || item.warehouse_code }} ({{ item.warehouse_code }})
          - Available: <strong>{{ item.quantity_available }}</strong>
        </div>

        <v-autocomplete
          v-model="form.work_order_id"
          :items="jobs"
          :loading="jobsLoading"
          :search="jobSearch"
          @update:search="onJobSearch"
          item-title="label"
          item-value="id"
          label="Job * (search by # or customer)"
          variant="outlined" density="compact"
          hide-details no-filter
          prepend-inner-icon="mdi-clipboard-list"
        />

        <v-text-field
          v-model.number="form.quantity"
          type="number" min="1" :max="item.quantity_available"
          label="Quantity to reserve"
          variant="outlined" density="compact" hide-details
          class="mt-3"
        />

        <v-textarea
          v-model="form.notes" rows="2"
          label="Notes (optional)"
          variant="outlined" density="compact" hide-details
          class="mt-3"
        />
      </v-card-text>
      <v-card-actions class="pa-3">
        <span v-if="errorText" class="text-error text-body-2 me-2">{{ errorText }}</span>
        <v-spacer />
        <v-btn variant="text" @click="close">Cancel</v-btn>
        <v-btn variant="elevated" color="amber-darken-2"
               :loading="saving" :disabled="!canSave" @click="save">
          Reserve
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch, reactive } from 'vue'
import axios from '@/api/client'
import { useNotify } from '@/composables/useNotify'

const { notify } = useNotify()

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  item: { type: Object, default: null },
})
const emit = defineEmits(['update:modelValue', 'saved'])
const visible = computed({
  get: () => props.modelValue,
  set: v => emit('update:modelValue', v),
})

const form = reactive({ work_order_id: null, quantity: 1, notes: '' })
const saving = ref(false)
const errorText = ref('')

const jobs = ref([])
const jobSearch = ref('')
const jobsLoading = ref(false)
let jobsTimer = null

watch(() => props.modelValue, v => {
  if (v) {
    form.work_order_id = null
    form.quantity = 1
    form.notes = ''
    errorText.value = ''
    jobs.value = []
    jobSearch.value = ''
  }
})

function onJobSearch(q) {
  jobSearch.value = q
  if (jobsTimer) clearTimeout(jobsTimer)
  if (!q || q.length < 2) return
  jobsTimer = setTimeout(async () => {
    jobsLoading.value = true
    try {
      const r = await axios.get('/jobs/search/', { params: { q, limit: 15 } })
      const arr = r.data?.results || r.data || []
      jobs.value = arr.map(j => ({
        id: j.id || j.work_order_id,
        label: `#${j.job_number || j.id} - ${j.customer_name || j.summary || ''}`.trim(),
      }))
    } catch (e) {
      console.warn('[ReserveDialog] job search failed', e)
      jobs.value = []
    } finally {
      jobsLoading.value = false
    }
  }, 200)
}

const canSave = computed(() =>
  !!form.work_order_id
  && Number(form.quantity) > 0
  && Number(form.quantity) <= (props.item?.quantity_available || 0)
  && !saving.value
)

async function save() {
  if (!canSave.value || !props.item) return
  saving.value = true
  errorText.value = ''
  try {
    await axios.post(`/parts/inventory/${props.item.id}/reserve/`, {
      quantity: form.quantity,
      work_order_id: form.work_order_id,
      notes: form.notes,
    })
    notify.success(`Reserved ${form.quantity} unit(s) for job #${form.work_order_id}`)
    emit('saved')
    close()
  } catch (e) {
    console.error('[ReserveDialog] save failed', e)
    errorText.value = e?.response?.data?.error || 'Could not reserve'
    notify.error(errorText.value)
  } finally {
    saving.value = false
  }
}

function close() { visible.value = false }
</script>
